import * as React from "react";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import Button from "@mui/material/Button";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { sidebarOptions, SidebarOption, getEffectiveSidebarOption } from "../../Data/SiderbarOptionsData";
import { useAppSelector } from "../../Redux/Hooks";
import { useLocation, useNavigate } from "react-router-dom";
import sendFirebaseEvent from "../../analytics/FirebaseAnalytics";
import styled from "styled-components";
import { device } from "../atoms/Utlis/media";
import ProfileSection from "../molecules/Dashboard/Navbar/ProfileSection";
import { ChevronDown, ChevronUp } from "lucide-react";
import { usePermissions } from "../../hooks/usePermissions";

const isPathMatching = (current: string, target: string) => {
  if (!current || !target) return false;
  const normCurrent = current.replace(/\/+$/, '') || '/';
  const normTarget = target.replace(/\/+$/, '') || '/';
  if (normTarget === '/dashboard') {
    return normCurrent === '/dashboard';
  }
  return normCurrent === normTarget || normCurrent.startsWith(normTarget + '/');
};

const MobileSidebarContainer = styled.div`
  display: none;

  @media ${device.tablet} {
    display: block;
  }
`;

export default function MobileSidebar() {
  const [open, setOpen] = React.useState(false);
  const [openDropdown, setOpenDropdown] = React.useState<string | null>(null);
  const location = useLocation();
  const {
    CalendarEventFlag,
    Package,
    UserEvent,
    UserTransaction,
    Quotation,
    WalletManagement,
    CustomerPaymentFlag,
    Orders,
    WhatsApp,
    Leads,
  } = useAppSelector((state) => state.flags);
  const navigate = useNavigate();
  const handleClick = (clickedTab: any) => {
    if (clickedTab.children?.length) {
      setOpenDropdown((current) =>
        current === clickedTab.key ? null : clickedTab.key
      );
      return;
    }

    sendFirebaseEvent(
      "Sidebar",
      "User_Interaction",
      "Button_click",
      clickedTab.key
    );
    navigate(clickedTab.redirection);
    setOpen(false);
  };

  const { isStudioOwner, hasPermission, hasModule } = usePermissions();

  const checkFlag = (key: string) => {
    // console.log("checking flag")
    if (key === "Events") {
      return !!(CalendarEventFlag || UserEvent);
    } else if (key === "CalendarEvent") {
      if (CalendarEventFlag) return true;
      else return false;
    } else if (key === "Package") {
      return Package;
    } else if (key === "UserEvent") {
      return UserEvent;
    } else if (key === "UserTransaction") {
      return UserTransaction;
    } else if (key === "Quotation") {
      return Quotation;
    } else if (key === "Orders") {
      return Orders;
    } else if (key === "WalletManagement") {
      return WalletManagement;
    } else if (key === "CustomerPaymentFlag") {
      return CustomerPaymentFlag;
    } else if (key === "WhatsApp") {
      return WhatsApp;
    } else if (key === "Leads") {
      return Leads;
    } else if (key === "RoleManagement") {
      return true;
    } else return true;
  };

  const isChildVisible = (opt: SidebarOption): boolean => {
    if (!checkFlag(opt.flag)) return false;
    if (isStudioOwner) return true;
    if (opt.key === 'Wallet Management' || opt.key === 'Studio Website') {
      return false;
    }

    const hasPerm = opt.anyPermission
      ? opt.anyPermission.some((p) => hasPermission(p))
      : opt.permission
      ? hasPermission(opt.permission)
      : true;

    const hasMod = opt.anyModule
      ? opt.anyModule.some((m) => hasModule(m))
      : opt.module
      ? hasModule(opt.module)
      : true;

    if (opt.permission || opt.anyPermission) {
      if (!hasPerm && (!opt.module && !opt.anyModule || !hasMod)) return false;
    }
    if ((opt.module || opt.anyModule) && !hasMod) return false;

    return true;
  };

  const isOptionVisible = (opt: SidebarOption): boolean => {
    if (!checkFlag(opt.flag)) return false;
    if (isStudioOwner) return true;
    if (opt.key === 'Wallet Management' || opt.key === 'Studio Website') {
      return false;
    }

    if (opt.children && opt.children.length > 0) {
      const anyChildVisible = opt.children.some((child) => isChildVisible(child));
      if (!anyChildVisible) return false;
      return true;
    }

    return isChildVisible(opt);
  };

  const toggleDrawer = (newOpen: boolean) => () => {
    setOpen(newOpen);
  };

  const DrawerList = (
    <Box sx={{ width: 250 }} role="presentation" className="pt-3 relative">
      <button
        className="absolute top-2 right-2 z-30 opacity-70"
        onClick={toggleDrawer(false)}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          version="1.1"
          xmlnsXlink="http://www.w3.org/1999/xlink"
          width={16}
          height={16}
          x={0}
          y={0}
          viewBox="0 0 329.269 329"
          xmlSpace="preserve"
          className=""
        >
          <g>
            <path
              d="M194.8 164.77 323.013 36.555c8.343-8.34 8.343-21.825 0-30.164-8.34-8.34-21.825-8.34-30.164 0L164.633 134.605 36.422 6.391c-8.344-8.34-21.824-8.34-30.164 0-8.344 8.34-8.344 21.824 0 30.164l128.21 128.215L6.259 292.984c-8.344 8.34-8.344 21.825 0 30.164a21.266 21.266 0 0 0 15.082 6.25c5.46 0 10.922-2.09 15.082-6.25l128.21-128.214 128.216 128.214a21.273 21.273 0 0 0 15.082 6.25c5.46 0 10.922-2.09 15.082-6.25 8.343-8.34 8.343-21.824 0-30.164zm0 0"
              fill="#000000"
              opacity={1}
              data-original="#000000"
            />
          </g>
        </svg>
      </button>
      <List>
        <ListItem>
          <ProfileSection />
        </ListItem>
        <Divider />

        {sidebarOptions.map((rawOption) => {
          const option = getEffectiveSidebarOption(
            rawOption,
            hasPermission,
            hasModule,
            isStudioOwner
          );
          const visibleChildren = option.children?.filter(isOptionVisible) || [];

          return (
            isOptionVisible(option) && (
              <React.Fragment key={option.key}>
                <ListItem disablePadding>
                  <ListItemButton onClick={() => handleClick(option)}>
                    <ListItemIcon>
                      <img className="w-6 h-6 object-contain" src={option.icon} alt="" />
                    </ListItemIcon>
                    <ListItemText primary={option.key} />
                    {visibleChildren.length ? (
                      <Box
                        component="span"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenDropdown((current) =>
                            current === option.key ? null : option.key
                          );
                        }}
                        sx={{ display: 'flex', alignItems: 'center', p: 0.5 }}
                      >
                        {openDropdown === option.key ? (
                          <ChevronUp size={18} />
                        ) : (
                          <ChevronDown size={18} />
                        )}
                      </Box>
                    ) : null}
                  </ListItemButton>
                </ListItem>
                {openDropdown === option.key &&
                  visibleChildren.map((child) => (
                    <ListItem key={child.key} disablePadding>
                      <ListItemButton
                        sx={{ pl: 5 }}
                        onClick={() => handleClick(child)}
                      >
                        <ListItemIcon>
                          <img
                            className="w-5 h-5 object-contain"
                            src={child.icon}
                            alt=""
                          />
                        </ListItemIcon>
                        <ListItemText primary={child.key} />
                      </ListItemButton>
                    </ListItem>
                  ))}
              </React.Fragment>
            )
          );
        })}
      </List>
      <Divider />
    </Box>
  );

  return (
    <MobileSidebarContainer>
      <button onClick={toggleDrawer(true)} className="w-fit">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          version="1.1"
          xmlnsXlink="http://www.w3.org/1999/xlink"
          width={28}
          height={28}
          x={0}
          y={0}
          viewBox="0 0 24 24"
          xmlSpace="preserve"
          className=""
        >
          <g>
            <path
              d="M3.5 7.5h17a1.5 1.5 0 1 0 0-3h-17a1.5 1.5 0 1 0 0 3zM20.5 10.5h-17a1.5 1.5 0 1 0 0 3h17a1.5 1.5 0 1 0 0-3zM20.5 16.5h-17a1.5 1.5 0 1 0 0 3h17a1.5 1.5 0 1 0 0-3z"
              fill="#000000"
              opacity={1}
              data-original="#000000"
              className=""
            />
          </g>
        </svg>
      </button>
      <Drawer anchor="right" open={open} onClose={toggleDrawer(false)}>
        {DrawerList}
      </Drawer>
    </MobileSidebarContainer>
  );
}
